import { FullScreenProvider } from '@/components/FullScreen';
import { Header } from '@/components/Header';
import { WasmLoadErrorFallback } from '@/components/WasmLoadErrorFallback';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { FullPageLoader } from '@analytical-alley/ui';
import { useCallback, useRef, useState } from 'react';
import { BudgetOptimizationSection } from '@/dashboards/BudgetOptimization/components/BudgetOptimizationSection';
import { BudgetOptimizationContextProvider } from '@/dashboards/BudgetOptimization/context';
import type { ZeroInvestmentAdjustmentInfo } from '@/dashboards/BudgetOptimization/core/budgetSessionRules';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import { OptimizationErrorModal } from '@/components/OptimizationErrorModal';
import { ModalWithContext } from './components/OutOfBoundsWarningModal/OutOfBoundsWarningWithContext';
import { ZeroInvestmentAdjustmentModal } from './components/ZeroInvestmentAdjustmentModal';

// A failed re-run keeps the last committed plan on screen; failures before the first commit
// are thrown to the route's error screen by the context provider.
const RunErrorModal = () => {
    const { error, recalculate, resetVariables, dismissError } =
        useBudgetOptimizationContext();
    if (!error) {
        return null;
    }
    return (
        <OptimizationErrorModal
            subject="the yearly budget"
            error={error}
            onRetry={recalculate}
            onClose={() => {
                resetVariables();
                dismissError();
            }}
        />
    );
};

export const BudgetOptimization = () => {
    const { optimizerInitError, optimizerReady } = useOptimizer();
    const portalRef = useRef<HTMLDivElement>(null);
    const [showOutOfBoundaryWarning, setShowOutOfBoundaryWarning] =
        useState(false);
    const [outOfBoundsChannel, setOutOfBoundsChannel] = useState<{
        channelName?: string;
        min?: number;
        max?: number;
    } | null>(null);
    const [modalResetKey, setModalResetKey] = useState(0);
    const [zeroInvestmentInfo, setZeroInvestmentInfo] =
        useState<ZeroInvestmentAdjustmentInfo | null>(null);

    const handleZeroInvestmentAdjustment = useCallback(
        (info: ZeroInvestmentAdjustmentInfo) => {
            setZeroInvestmentInfo(info);
        },
        [],
    );

    const handleCloseZeroInvestmentModal = useCallback(() => {
        setZeroInvestmentInfo(null);
    }, []);

    if (optimizerInitError) {
        return (
            <WasmLoadErrorFallback
                dashboardName="Budget Optimization"
                error={optimizerInitError}
            />
        );
    }

    if (!optimizerReady) {
        return <FullPageLoader />;
    }

    return (
        <BudgetOptimizationContextProvider
            onZeroInvestmentAdjustment={handleZeroInvestmentAdjustment}
        >
            <Header
                className="pb-6 px-8 lg:px-16 pt-6"
                title="Yearly Budget Optimization"
                tooltip={
                    <span>
                        Strategic budget planner. <br />
                        Define your budget for the upcoming financial year.<br />
                        Set your target and simulate different media investment
                        scenarios to find the optimal balance across media
                        channels for your given goal and constraints. <br />
                        Use our ROI charts and predicted KPI tables to validate
                        the results before committing to your plans.
                    </span>
                }
                sticky
            />
            <div className="pb-8 px-8 lg:px-16">
                <div className="transition-all duration-300" ref={portalRef} />
                <FullScreenProvider portalRef={portalRef}>
                    <BudgetOptimizationSection
                        onOutOfBoundsChannel={(channelInfo) => {
                            setShowOutOfBoundaryWarning(true);
                            setOutOfBoundsChannel(channelInfo);
                        }}
                        modalResetKey={modalResetKey}
                    />
                </FullScreenProvider>
            </div>
            <ModalWithContext
                show={showOutOfBoundaryWarning}
                channelInfo={outOfBoundsChannel}
                onClose={() => {
                    setShowOutOfBoundaryWarning(false);
                    setOutOfBoundsChannel(null);
                    setModalResetKey((k) => k + 1);
                }}
            />
            <ZeroInvestmentAdjustmentModal
                show={!!zeroInvestmentInfo}
                channelName={zeroInvestmentInfo?.channelName}
                previousBudget={zeroInvestmentInfo?.previousBudget}
                adjustedBudget={zeroInvestmentInfo?.adjustedBudget}
                onClose={handleCloseZeroInvestmentModal}
            />
            <RunErrorModal />
        </BudgetOptimizationContextProvider>
    );
};
